import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Star, Globe, Users, Award, Shield, CheckCircle, ArrowRight, Truck, Heart, Target, Building2 } from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100
    }
  }
};

const Services = () => {
  const reasons = [
    {
      icon: Users,
      title: 'Built on Proven Medical Expertise',
      description: 'Built on proven medical expertise, Mediquip provides comprehensive healthcare solutions founded on extensive clinical and corporate healthcare experience from our leadership team.',
      highlights: ['Proven medical expertise', 'Clinical excellence foundation', 'Global healthcare experience'],
      color: 'teal',
      image: "https://images.unsplash.com/photo-1678695972687-033fa0bdbac9"
    },
    {
      icon: Globe,
      title: 'Multilingual Market Expertise',
      description: "Mediquip operates across both Anglophone and Francophone countries, understanding the distinct regulatory environments, cultural nuances, and healthcare challenges faced by medical professionals in diverse linguistic territories.",
      highlights: ['Anglophone market expertise', 'Francophone market understanding', 'Cultural and regulatory competence'],
      color: 'blue',
      image: "https://images.unsplash.com/photo-1666886573553-6548db92db79"
    },
    {
      icon: Award,
      title: 'Comprehensive Service Portfolio',
      description: "Our three core services - medical consumables distribution, healthcare consulting, and business development - provide end-to-end solutions for healthcare practitioners and organizations seeking to optimize operations and expand capabilities.",
      highlights: ['Medical consumables distribution', 'Healthcare consulting services', 'Strategic business development'],
      color: 'green',
      image: "https://images.unsplash.com/photo-1536064479547-7ee40b74b807"
    },
    {
      icon: Shield,
      title: 'Strategic Global Partnerships',
      description: 'Mediquip maintains official partnerships with leading global healthcare companies including Lapu Medical , Wizbiotech, Zutron Pharmaceutical and serves as a sub-dealer for Terumo Blood and Cell Technologies, ensuring access to premium medical solutions.',
      highlights: ['Lapu Medical Technology Partnership', 'Wizbiotech collaboration','Zutron Pharmaceutical', 'Terumo sub-dealer relationship'],
      color: 'purple',
      image: "https://images.pexels.com/photos/33733645/pexels-photo-33733645.jpeg"
    }
  ];

  const services = [
    {
      icon: Truck,
      title: 'Medical Consumables Distribution',
      description: 'Comprehensive distribution of premium-quality medical consumables directly to healthcare practitioners across West Africa',
      features: ['Direct-to-practitioner delivery', 'Quality assured products', 'Reliable supply chain', 'Competitive pricing']
    },
    {
      icon: Users,
      title: 'Healthcare Consulting',
      description: 'Expert consulting services to help healthcare facilities optimize operations and improve patient care delivery',
      features: ['Operational efficiency consulting', 'Healthcare system optimization', 'Best practice implementation', 'Strategic planning support']
    },
    {
      icon: Building2,
      title: 'Business Development',
      description: 'Strategic business development services to help healthcare organizations grow and expand their capabilities',
      features: ['Market expansion strategies', 'Partnership development', 'Growth planning', 'Revenue optimization']
    }
  ];

  return (
    <div className="pt-20">
      {/* Page Header */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-teal-50/30">
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Comprehensive Healthcare Solutions
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Mediquip delivers three core services to healthcare practitioners and organizations 
              across Anglophone and Francophone countries: medical consumables distribution, 
              expert healthcare consulting, and strategic business development solutions.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ amount: 0.2 }}
          variants={containerVariants}
        >
          <div className="text-center mb-16">
            <motion.h2 className="text-3xl font-bold text-slate-900 mb-4" variants={itemVariants}>
              Our Core Services
            </motion.h2>
            <motion.p className="text-lg text-slate-600 max-w-2xl mx-auto" variants={itemVariants}>
              Comprehensive healthcare solutions designed to meet the unique needs of healthcare practitioners 
              across both English-speaking and French-speaking markets
            </motion.p>
          </div>

          <motion.div className="grid md:grid-cols-2 gap-8 mb-20" variants={containerVariants}>
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  className="bg-slate-50 rounded-xl p-8 border border-slate-100 hover:shadow-lg transition-all duration-300"
                  variants={itemVariants}
                >
                  <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">{service.title}</h3>
                  <p className="text-slate-600 mb-6">{service.description}</p>
                  <div className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0" />
                        <span className="text-slate-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>
      </section>

      ---

      {/* Why Choose Us */}
      <section className="py-20 bg-slate-50">
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ amount: 0.2 }}
          variants={containerVariants}
        >
          <div className="text-center mb-16">
            <motion.h2 className="text-3xl font-bold text-slate-900 mb-4" variants={itemVariants}>
              Why Healthcare Professionals Choose Mediquip
            </motion.h2>
            <motion.p className="text-lg text-slate-600 max-w-2xl mx-auto" variants={itemVariants}>
              Four key differentiators that make Mediquip the preferred partner for healthcare practitioners 
              and organizations across Anglophone and Francophone markets
            </motion.p>
          </div>

          <motion.div className="space-y-20" variants={containerVariants}>
            {reasons.map((reason, index) => {
              const Icon = reason.icon;
              const isEven = index % 2 === 0;
              
              const colorClasses = {
                teal: 'bg-teal-100 text-teal-600 border-teal-200',
                blue: 'bg-blue-100 text-blue-600 border-blue-200',
                green: 'bg-green-100 text-green-600 border-green-200',
                purple: 'bg-purple-100 text-purple-600 border-purple-200'
              };

              return (
                <motion.div
                  key={index}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${
                    !isEven ? 'lg:grid-flow-col-dense' : ''
                  }`}
                  variants={itemVariants}
                >
                  {/* Content */}
                  <div className={`space-y-6 ${!isEven ? 'lg:col-start-2' : ''}`}>
                    <div className="flex items-center space-x-4">
                      <div className={`w-16 h-16 ${colorClasses[reason.color]} rounded-xl flex items-center justify-center border-2`}>
                        <Icon className="w-8 h-8" />
                      </div>
                      <div className="text-sm font-medium text-slate-500">
                        0{index + 1}
                      </div>
                    </div>
                    
                    <h3 className="text-3xl font-bold text-slate-900">
                      {reason.title}
                    </h3>
                    
                    <p className="text-lg text-slate-600 leading-relaxed">
                      {reason.description}
                    </p>
                    
                    <div className="space-y-3">
                      {reason.highlights.map((highlight, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <CheckCircle className={`w-5 h-5 ${reason.color === 'teal' ? 'text-teal-600' : reason.color === 'blue' ? 'text-blue-600' : reason.color === 'green' ? 'text-green-600' : 'text-purple-600'}`} />
                          <span className="text-slate-700 font-medium">{highlight}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Image */}
                  <div className={`relative ${!isEven ? 'lg:col-start-1' : ''}`}>
                    <motion.div
                      className="relative z-10"
                      initial={{ scale: 0.9, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ amount: 0.5 }}
                      transition={{ duration: 0.8 }}
                    >
                      <img
                        src={reason.image}
                        alt={reason.title}
                        className="w-full h-80 object-cover rounded-2xl shadow-xl"
                      />
                    </motion.div>
                    
                    {/* Background decoration */}
                    <div className={`absolute inset-0 ${
                      reason.color === 'teal' ? 'bg-gradient-to-r from-teal-100 to-teal-50' :
                      reason.color === 'blue' ? 'bg-gradient-to-r from-blue-100 to-blue-50' :
                      reason.color === 'green' ? 'bg-gradient-to-r from-green-100 to-green-50' :
                      'bg-gradient-to-r from-purple-100 to-purple-50'
                    } rounded-2xl transform ${isEven ? 'rotate-3' : '-rotate-3'} scale-105 opacity-30 -z-10`}></div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>
      </section>

      ---

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <motion.div
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ amount: 0.5 }}
          transition={{ duration: 0.6 }}
        >
          <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-8 lg:p-12 border border-slate-100">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
              <Heart className="w-8 h-8 text-teal-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Ready to Transform Your Healthcare Supply Chain?
            </h2>
            <p className="text-xl text-slate-700 leading-relaxed mb-8">
              Partner with Mediquip to access premium medical consumables, reliable distribution, 
              and innovative solutions tailored to your healthcare facility's unique needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                Get Started Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to="/leadership"
                className="inline-flex items-center px-8 py-4 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:border-teal-600 hover:text-teal-600 transition-all duration-200"
              >
                Meet Our Team
              </Link>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default Services;